import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  input,
  OnDestroy,
  OnInit,
  Output,
  output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { CecBreakpointPipe } from 'src/app/controls/pipes/cec-breakpoint.pipe';
import {
  AsyncPipe,
  NgFor,
  NgForOf,
  NgIf,
  NgTemplateOutlet,
} from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MenuItem } from 'src/app/controls/menu/models/menu-item.interface';
import { NavigationItem } from 'src/app/controls/menu/models/navigation-item.interface';
import { MenuProvider } from '../services/menu.provider';
import { MenuTrigger } from '../models/menu-trigger.interface';
import { MenuItemComponent } from './menu-item/menu-item.component';
import { NavigationService } from 'src/app/common/components/navigation-bar/services/navigation-bar.service';

@Component({
  selector: 'cec-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    CecBreakpointPipe,
    AsyncPipe,
    MatIconModule,
    MatMenuModule,
    FormsModule,
    MatDividerModule,
    MatExpansionModule,
    MatButtonModule,
    MatListModule,
    MenuItemComponent
  ],
  providers: [CecBreakpointPipe],
})
export class MenuComponent {
  
  /** Title input reference */
  title = input<string | undefined>(undefined);
  
  /** Datasource reference */
  public dataSource = input<MenuProvider | undefined>(undefined);

  /** Router reference */
  private router = inject(Router);

  /** Activated route reference */
  private route = inject(ActivatedRoute);

  /** Navigation service reference */
  private service = inject(NavigationService);

  /** Breakpoint pipe reference */
  private breakpointPipe = inject(CecBreakpointPipe);

  /** Menu alignment reference */
  public alignment = input<'left' | 'right'>('left');

  /** Subscription for unsubscribing reference */
  private sub = new Subscription();

  /** Navigation item reference */
  protected navigation: NavigationItem | undefined = undefined;

  /** Root navigation items reference */
  protected rootItems: MenuItem[] | undefined = [];

  /** Close side nav emitter */
  public onCloseSideNav = output<void>();

  /** Button click item output emitter */
  public onItemClicked = output<MenuTrigger>();

  /** Obtained menu trigger reference */
  protected obtainedMenu: MatMenu | undefined = undefined;

  /** Change detector reference */
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    /** if window size changes to normal size, hide side navigation menu */
    this.sub = this.breakpointPipe
      .transform(['xs', 'sm', 'md'])
      .subscribe((result) => {
        if (!result) {
          this.service.closeSideNav.next();
          this.onCloseSideNav.emit();
        }
      });

    /** loads data with whole navigation item */
    this.loadData(true);
  }

  ngOnDestroy(): void {
    /** unsubscribes from observables */
    this.sub.unsubscribe();
  }

  /**
   * Loads the navigation item and root items
   * @param loadAll to decide, whether to load whole navigation item or not
   */
  protected loadData(loadAll = false): void {
    /** if datasource is not provided, do nothing */
    if (!this.dataSource()) {
      return;
    }

    /** gets menu items from datasource */
    this.sub.add(
      this.dataSource()
        .getMenuItems()
        .subscribe((data: NavigationItem) => {
          /** if true, load whole navigation item (during initialization) */
          if (loadAll) {
            this.navigation = data;
          }

          /** load root items */
          this.rootItems = data?.items;
        })
    );
  }

  /**
   * Executes desired logic after clicking on item
   * @param item the selected item
   * @param rootItem the selected root item
   */
  protected onClickItem(trigger: MenuTrigger): void {
    /** closes the side navigation if opened */
    this.service.closeSideNav.next();
    this.onCloseSideNav.emit();

    /** if url parameter is present, navigates to desired url */
    if (trigger.item.url) {
      this.router.navigate([trigger.item.url], { relativeTo: this.route });
    }

    /** emits the item */
    this.onItemClicked.emit(trigger);
  }

  /**
   * Sets the obtained menu trigger
   * @param menuTrigger the menu trigger to set 
   */
  protected onTrigger(menuTrigger: MatMenu): void {
    /** sets the menu trigger */
    this.obtainedMenu = menuTrigger;

    /** detects the changes */
    this.cdr.detectChanges();
  }
}
